import {
  OrderLine,
  OrderLineLogo,
} from "../../../../core/models/entities/Order";

export interface OrderLineGroup {
  leader: OrderLine;
  variants: OrderLine[];
}

const TYPED_VALUE_SUFFIX = /\s*\(([^)]+)\)\s*$/;

export function extractTypedValue(line: OrderLine): string | undefined {
  // For NumberLogo digit-split lines the title carries the per-digit value
  // (e.g. "(1)", "(2)") while typedNumberValue carries the original full number
  // ("12"). Prefer typedNumberValue so callers see one logical entry per typed
  // number rather than per emitted digit line.
  if (line.typedNumberValue) return line.typedNumberValue;
  const match = line.title?.match(TYPED_VALUE_SUFFIX);
  if (!match) return undefined;
  // Some call sites wrap the typed label in literal quotes — strip them for display.
  return match[1].replace(/^["']+|["']+$/g, "");
}

function stripTypedValue(title: string | undefined): string {
  if (!title) return "";
  return title.replace(TYPED_VALUE_SUFFIX, "").trim();
}

function isRealSku(productNo: string | undefined): boolean {
  if (!productNo) return false;
  if (productNo === "From Storage") return false;
  if (productNo === "To Storage") return false;
  if (productNo === "Additional Fee") return false;
  if (productNo === "AdditionalFee") return false;
  return true;
}

function isLogoType(line: OrderLine): boolean {
  return line.type === "Logo" || line.product?.productType === "Logo";
}

function canGroupLine(line: OrderLine): boolean {
  return isLogoType(line) && isRealSku(line.product?.productNo);
}

// Grouping key uses relatedTo + the logical logo identity. When baseLogo.id
// is available we use that, so two genuinely-different logos that happen to
// share a title (different color, font, size) stay apart. baseLogo.id is the
// same for:
//   - NameLogo variants with different typed values (same logo, different names)
//   - NumberLogo per-digit splits (same parent package, different digit SKUs)
// When baseLogo isn't present on the line, we fall back to stripped title —
// a reasonable proxy, though coarser.
function groupKey(line: OrderLine): string {
  const parent = line.relatedTo ?? 0;
  const baseLogoId = (line as OrderLineLogo).baseLogo?.id;
  if (baseLogoId != null && baseLogoId !== 0) {
    return `${parent}|logo:${baseLogoId}`;
  }
  return `${parent}|title:${stripTypedValue(line.title)}`;
}

// When variants disagree on SKU (digit-split NumberLogos) we prefer the parent
// baseLogo's SKU as the leader identity — that's the "package" SKU the user
// actually placed the order against.
function resolveLeaderProductNo(group: OrderLineGroup): string {
  const firstNo = group.leader.product?.productNo ?? "";
  const allSame = group.variants.every(
    (v) => (v.product?.productNo ?? "") === firstNo,
  );
  if (allSame) return firstNo;
  const baseSku = (group.leader as OrderLineLogo).baseLogo?.product?.sku;
  return baseSku ?? firstNo;
}

export function groupOrderLinesForDisplay(
  orderlines: OrderLine[],
): OrderLineGroup[] {
  const groups: OrderLineGroup[] = [];
  const groupIndex = new Map<string, OrderLineGroup>();
  const hiddenInGroup = new Set<number>();

  for (const line of orderlines) {
    if (!canGroupLine(line)) continue;

    const key = groupKey(line);
    const existing = groupIndex.get(key);
    if (existing) {
      existing.variants.push(line);
      hiddenInGroup.add(line.id);
    } else {
      groupIndex.set(key, { leader: line, variants: [line] });
    }
  }

  for (const line of orderlines) {
    if (hiddenInGroup.has(line.id)) continue;

    if (canGroupLine(line)) {
      const key = groupKey(line);
      const g = groupIndex.get(key);
      if (g && g.variants.length > 1) {
        const totalQty = g.variants.reduce((s, v) => s + (v.quantity ?? 0), 0);
        const totalPrice = g.variants.reduce((s, v) => s + (v.price ?? 0), 0);
        const totalAdjustedPrice = g.variants.reduce(
          (s, v) => s + (v.adjustedPrice ?? 0),
          0,
        );
        const leaderProductNo = resolveLeaderProductNo(g);
        const leader: OrderLine = {
          ...g.leader,
          title: stripTypedValue(g.leader.title),
          quantity: totalQty,
          price: totalPrice,
          adjustedPrice: totalAdjustedPrice,
          product: {
            ...g.leader.product,
            productNo: leaderProductNo,
            productName: stripTypedValue(g.leader.product?.productName),
          },
        };
        groups.push({ leader, variants: g.variants });
        continue;
      }
    }

    groups.push({ leader: line, variants: [line] });
  }

  return groups;
}
